import { useState, createContext, useEffect } from 'react'
import { toast } from 'react-toastify'

import { useTableData } from '@/hooks/useTableData'

import { inspectionBreakdownService } from '@/services/Inspection/InspectionBreakdownService'
import { inspectionService } from '@/services/Inspection/inspectionService'
import { vehicleServiceV2 } from '@/services/utils/v2/vehicleService'
import { companyService } from '@/services/Parametrization/companyService'
import { categoryService } from '@/services/Parametrization/categoryService'
import { utilityPreparationService } from '@/services/Preparation/utilityPreparationService'
import { inspectionAccessoriesService } from '@/services/Inspection/InspectionAccessories'
import { utilityService } from '@/services/Parametrization/utilityService'
import { inspectionServiceV2 } from '@/services/Inspection/v2/InspectionService'
import { inspectionPhotoServiceV2 } from '@/services/Inspection/v2/inspectionPhotoService'

import {
  IDisableInspectionPhotoParams,
  IFinalizeInspectionParams,
  IInspectionChecklistPostParams,
  IInspectionChecklistPutParams,
  InspectionPhotoParametrizationResponse,
  IReproveInspectionPhotoParams,
  IUpdateInspectionPhotoObservation,
} from '@/types/inspection.types'
import { removePropertyEmpty } from '../utils'
import { CONSTANTS, PHOTOS_PATHS, SUCCESS_STATUS } from '@/utils/CONSTANTS'
import { generateFilename, getFileExtension } from '@/utils/helpers'
import { MESSAGES } from '@/utils/constants/messages'
import { ESituacaoVistoria } from '@/types/enums/inspections'

interface IInspection {
  children: React.ReactNode
}

type InspectionContextData = {
  INSPECTION_LIST: any
  INTERPRISE_LIST: any
  loading: any
  ACCESSORIES_LIST: any
  VEHICLE_PHOTO_DATA: any
  SELECTED_INSPECTION_DETAILS: any
  BREAKDOWN_PHOTO_DATA: any
  OWNER_DATA: any
  INSPECTION_DATA: any
  FLEET_LIST: any
  CATEGORY_LIST: any
  CATEGORY_OPTIONS: any
  BOARD_LIST: any
  BOARD_OPTIONS: any
  MODEL_LIST: any
  MODEL_OPTIONS: any
  MODEL_CATEGORY_OPTIONS: any
  MODEL_CATEGORY_LIST: any
  CATEGORY_INSPECTION_LIST: any
  IS_LEGACY_INSPECTION: boolean
  IS_INSPECTION_REMAKE: boolean
  HISTORY_CR: any[]
  GET_INSPECTION: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_INSPECTION_BY_ID: (id: any) => Promise<void>
  GET_INTERPRISE: (params: any) => Promise<any>
  GET_ACCESSORIES: (id: string) => Promise<void>
  GET_VEHICLE_PHOTOS: (id: any, version?: 'v1' | 'v2') => Promise<void>
  GET_BREAKDOWN_PHOTOS: (id: any) => Promise<void>
  GET_INSPECTION_HISTORY: (id: any) => Promise<void>
  SET_REFUSE_ALL: (variable: any, id: any) => Promise<void>
  SET_REFUSED: (variable: any, params: any) => Promise<void>
  SET_OWNER: (params: any) => void
  SET_INSPECTION: (params: any) => void
  SET_VEHICLE_PHOTO_DATA: (params: any) => void
  SET_BREAKDOWN_PHOTO_DATA: (params: any) => void
  setLoading: (bool: boolean) => void
  GET_FLEET: (params?: any, isFilterApplied?: boolean) => {}
  GET_MODEL: (params?: any) => {}
  SET_EVALUETE_FINISHED: (params: any) => Promise<any>
  GET_CATEGORY_INSPECTION: (params?: any, isFilterApplied?: boolean) => Promise<void>
  GET_SELECTS: (params?: any) => Promise<void>
  GET_SELECTED_INSPECTION_DETAILS: (ids: any[]) => void
  GET_SUBCATEGORIES: () => void
  GET_PHOTO_PARAMETRIZATION: (id: any) => Promise<InspectionPhotoParametrizationResponse>
  GET_HISTORY_CR: (id: string) => Promise<void>
  GET_SIGNATURE: (id: string) => Promise<void>
  CREATE_INSPECTION: (data: any) => Promise<void>
  UPDATE_INSPECTION: (id: any, data: any) => Promise<void>
  CREATE_FLEET_INSPECTION: (data: any) => Promise<void>
  START_INSPECTION: (id: any) => Promise<void>
  UPDATE_INSPECTION_EXECUTION: (id: string, data: any) => Promise<void>
  UPDATE_INSPECTION_OWNER: (id: string, data: any) => Promise<void>
  CLONE_INSPECTION: (data: any) => Promise<void>
  ADD_INSPECTION_PHOTO: (file: File, params: any) => Promise<void>
  UPDATE_INSPECTION_PHOTO: (fileName: any, fullPath: any, params: any) => Promise<void>
  DISABLE_INSPECTION_PHOTO: (params: IDisableInspectionPhotoParams) => Promise<void>
  EXECUTE_INSPECTION: (params: IFinalizeInspectionParams) => Promise<void>
  REPROVE_INSPECTION_PHOTOS: (params: IReproveInspectionPhotoParams) => Promise<void>
  CREATE_INSPECTION_CHECKLIST: (params: IInspectionChecklistPostParams) => Promise<void>
  UPDATE_INSPECTION_CHECKLIST_STATUS: (params: IInspectionChecklistPutParams) => void
  UPDATE_INSPECTION_PHOTO_OBSERVATION: (params: IUpdateInspectionPhotoObservation) => Promise<void>
}

export const InspectionContext = createContext({} as InspectionContextData)

export function InspectionProvider({ children }: IInspection) {
  const [VEHICLE_PHOTO_DATA, SET_VEHICLE_PHOTO_DATA] = useState([])
  const [BREAKDOWN_PHOTO_DATA, SET_BREAKDOWN_PHOTO_DATA] = useState([])
  const [INSPECTION_LIST, SET_INSPECTION_LIST] = useState([])
  const [INTERPRISE_LIST, SET_INTERPRISE_LIST] = useState([])
  const [ACCESSORIES_LIST, SET_ACCESSORIES_LIST] = useState([])
  const [FLEET_LIST, SET_FLEET_LIST] = useState([])
  const [CATEGORY_LIST, SET_CATEGORY_LIST] = useState<any>({})
  const [CATEGORY_OPTIONS, SET_CATEGORY_OPTIONS] = useState([])
  const [BOARD_LIST, SET_BOARD_LIST] = useState([])
  const [BOARD_OPTIONS, SET_BOARD_OPTIONS] = useState([])
  const [MODEL_LIST, SET_MODEL_LIST] = useState([])
  const [MODEL_OPTIONS, SET_MODEL_OPTIONS] = useState([])
  const [MODEL_CATEGORY_LIST, SET_MODEL_CATEGORY_LIST] = useState([])
  const [MODEL_CATEGORY_OPTIONS, SET_MODEL_CATEGORY_OPTIONS] = useState([])
  const [SELECTED_INSPECTION_DETAILS, SET_SELECTED_INSPECTION_DETAILS] = useState([])
  const [CATEGORY_INSPECTION_LIST, SET_CATEGORY_INSPECTION_LIST] = useState([])
  const [OWNER_DATA, SET_OWNER_DATA] = useState(null)
  const [INSPECTION_DATA, SET_INSPECTION] = useState(null)
  const [loading, setLoading] = useState(false)
  const [IS_LEGACY_INSPECTION, SET_IS_LEGACY_INSPECTION] = useState(false)
  const [IS_INSPECTION_REMAKE, SET_IS_INSPECTION_REMAKE] = useState(false)
  const [HISTORY_CR, SET_HISTORY_CR] = useState<any[]>([])

  const { PER_PAGE, CURRENTE_PAGE, SORT_FIELD, SORT_ASCENDING } = useTableData()

  useEffect(() => {
    SET_DATA_SELECTS()
  }, [CATEGORY_LIST, BOARD_LIST, MODEL_LIST, MODEL_CATEGORY_LIST])

  async function GET_SELECTED_INSPECTION_DETAILS(params: any) {
    try {
      setLoading(true)
      const res: any = await inspectionService.getInspectionListByIds(params)
      SET_SELECTED_INSPECTION_DETAILS(res.data.data)
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setLoading(false)
    }
  }

  async function GET_HISTORY_CR(id: string) {
    SET_HISTORY_CR([])
    try {
      const res: any = await inspectionServiceV2.getHistoryCR(id)
      SET_HISTORY_CR(res.data.data ?? [])
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_INSPECTION(params: any, isFilterApplied?: boolean) {
    try {
      const payloadSubmitted = {
        PageSize: PER_PAGE,
        Page: CURRENTE_PAGE,
        SortField: SORT_FIELD,
        SortAscending: SORT_ASCENDING,
        ...params,
      }
      const res: any = await inspectionServiceV2.get(payloadSubmitted)
      const inspections = res.data.data.paginacao.rows

      if (!inspections?.length) {
        SET_INSPECTION_LIST([])
        return toast.warning(CONSTANTS.filter.empty)
      }
      if (inspections?.length && isFilterApplied) toast.success(CONSTANTS.filter.success)

      SET_INSPECTION_LIST(res.data.data)
      return res.data.data
    } catch (err) {
      toast.error('Não foi possível consultar os dados. Tente novamente.')
    }
  }

  async function GET_INSPECTION_BY_ID(id: any) {
    try {
      const responseInspection: any = await inspectionServiceV2.getById(id)
      const inspection = responseInspection?.data?.data
      SET_INSPECTION(inspection)
      VERIFY_LEGACY_INSPECTION(inspection?.tipoAvaliacaoId)
      CHECK_INSPECTION_REMAKE(inspection)
      return inspection
    } catch (e: any) {
      toast.error(e.message)
      CLEAR_INSPECTION()
    }
  }

  const CLEAR_INSPECTION = () => {
    SET_INSPECTION(null)
    SET_IS_LEGACY_INSPECTION(false)
    SET_IS_INSPECTION_REMAKE(false)
  }

  //TODO: verificar regra para vistoria legada
  function VERIFY_LEGACY_INSPECTION(tipoAvaliacaoId: string) {
    if (!tipoAvaliacaoId) {
      SET_IS_LEGACY_INSPECTION(true)
      return
    }
    SET_IS_LEGACY_INSPECTION(false)
  }

  async function GET_FLEET(params?: any, isFilterApplied?: boolean) {
    try {
      const payload = {
        ...params,
        Page: CURRENTE_PAGE,
        PageSize: PER_PAGE,
        SortAscending: SORT_ASCENDING,
        SortField: SORT_FIELD,
      }
      const res: any = await vehicleServiceV2.getFrota(removePropertyEmpty(payload))
      const fleet = res.data.data

      if (!fleet.rows?.length) {
        SET_FLEET_LIST([])
        return toast.warning(CONSTANTS.filter.empty)
      }
      if (fleet.rows?.length && isFilterApplied) toast.success(CONSTANTS.filter.success)

      SET_FLEET_LIST(fleet)
    } catch (err) {
      toast.error('Não foi possível consultar os dados. Tente novamente.')
    }
  }

  async function GET_CATEGORY_INSPECTION(params?: any, isFilterApplied?: boolean) {
    try {
      const res: any = await categoryService.categoriaVistoria(params)
      const cVitoria = res.data.data

      if (!cVitoria?.rows?.length) toast.warning(CONSTANTS.filter.empty)
      if (cVitoria?.rows?.length && isFilterApplied) toast.success(CONSTANTS.filter.success)

      SET_CATEGORY_INSPECTION_LIST(cVitoria)
      return cVitoria
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_INTERPRISE(params?: any) {
    try {
      const res: any = await companyService.get(params)
      const interprise = res.data.data
      SET_INTERPRISE_LIST((s) => interprise)
      return interprise
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_BREAKDOWN_PHOTOS(id: any) {
    try {
      const res: any = await inspectionBreakdownService.getBreakdownPhotos(id)
      SET_BREAKDOWN_PHOTO_DATA(res.data.data)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_VEHICLE_PHOTOS(id: any, apiVersion: 'v1' | 'v2' = 'v1') {
    try {
      const photoRequest = {
        v1: (vistoriaId: string) => inspectionBreakdownService.getVehiclePhotos(vistoriaId),
        v2: (vistoriaId: string) => inspectionPhotoServiceV2.getPhotoParametrization(vistoriaId),
      }
      const res: any = await photoRequest[apiVersion](id)
      SET_VEHICLE_PHOTO_DATA(res.data.data)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_ACCESSORIES(id: string) {
    try {
      const res: any = await inspectionAccessoriesService.getInspectionAccessories(id)
      SET_ACCESSORIES_LIST(res.data.data)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_INSPECTION_HISTORY(id: string) {
    try {
      const res: any = await inspectionService.getHistoric(id)
      return res.data.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function SET_REFUSED(variable: any, params: any) {
    try {
      if (variable === 'old') {
        await inspectionBreakdownService.setBreakdownRefused(params)
        await GET_BREAKDOWN_PHOTOS(params.vistoriaId)
        return
      }

      await inspectionBreakdownService.setVehiclePhotoRefused(params)
      await GET_VEHICLE_PHOTOS(params.vistoriaId)

      toast.success('Reprovação realizada.')
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function SET_REFUSE_ALL(variable: any, params: any) {
    try {
      if (variable === 'old') {
        await inspectionBreakdownService.setBreakdownRefusedAll(params)
        GET_BREAKDOWN_PHOTOS(params.vistoriaId)
      } else {
        await inspectionBreakdownService.setVehiclePhotoRefused(params)
        GET_VEHICLE_PHOTOS(params.vistoriaId)
      }
      toast.success('Suas reprovações foram concluídas')
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function SET_EVALUETE_FINISHED(params: any) {
    try {
      const response = await inspectionServiceV2.finalizeEvaluation(params)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_MODEL(params?: any) {
    try {
      const res: any = await utilityService.getModel(params)
      const models = res.data.data

      if (!models?.length) {
        SET_MODEL_LIST([])
        return toast.warning('Não há modelos para esta marca')
      }

      SET_MODEL_LIST(res.data.data)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_SUBCATEGORIES() {
    try {
      const res: any = await utilityService.getModelCategory()
      const subcategories = res.data.data

      if (!subcategories?.length) {
        SET_MODEL_CATEGORY_LIST([])
        return toast.warning('Não há subcategorias.')
      }

      SET_MODEL_CATEGORY_LIST(res.data.data)
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_SIGNATURE(id: string) {
    try {
      const res: any = await inspectionServiceV2.getSignature(id)
      return res.data.data
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_SELECTS() {
    if (CATEGORY_LIST?.rows?.length && BOARD_LIST.length && MODEL_CATEGORY_LIST.length) return null

    try {
      const res: any = await Promise.all([
        categoryService.get({}),
        utilityPreparationService.getBoard(),
        utilityService.getModelCategory(),
      ])

      SET_CATEGORY_LIST(() => res[0].data.data)
      SET_BOARD_LIST(() => res[1].data.data)
      SET_MODEL_CATEGORY_LIST(() => res[2].data.data)
      return res
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function SET_DATA_SELECTS() {
    let arrOfCategories: any = []
    let arrOfBoards: any = []
    let arrOfModels: any = []
    let arrOfModelsCategory: any = []

    CATEGORY_LIST?.rows?.forEach((item: any) => {
      arrOfCategories.push(item?.nome)
    })
    BOARD_LIST.forEach((item: any) => {
      arrOfBoards.push(item?.descricao)
    })
    MODEL_LIST.forEach((item: any) => {
      arrOfModels.push({
        descricao: `${item?.nome} - ${item?.codigoFipe}`,
        id: item.id,
        montadoraId: item.montadoraId,
      })
    })
    MODEL_CATEGORY_LIST.forEach((item: any) => {
      arrOfModelsCategory.push(item)
    })

    await SET_CATEGORY_OPTIONS(arrOfCategories)
    await SET_BOARD_OPTIONS(arrOfBoards)
    await SET_MODEL_OPTIONS(arrOfModels)
    await SET_MODEL_CATEGORY_OPTIONS(arrOfModelsCategory)
  }

  async function SET_OWNER(params: any) {
    const mountDataOwner: any = {
      nome: params?.proprietario,
      cnpjCpf: params?.proprietarioCnpjCpf,
      celular: params?.proprietarioCelular,
      email: params?.proprietarioEmail,
    }
    SET_OWNER_DATA(mountDataOwner)
  }

  async function CREATE_INSPECTION(data: any) {
    try {
      const response: any = await inspectionServiceV2.post(data)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function UPDATE_INSPECTION(id: any, data: any) {
    try {
      const response: any = await inspectionService.put(id, data)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function CREATE_FLEET_INSPECTION(data: any) {
    try {
      const response: any = await inspectionServiceV2.postFrota(data)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function START_INSPECTION(id: any) {
    try {
      const response: any = await inspectionServiceV2.iniciar(id)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function UPDATE_INSPECTION_EXECUTION(id: string, params: any) {
    try {
      const response: any = await inspectionServiceV2.putRealizar(id, params)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function UPDATE_INSPECTION_OWNER(id: string, params: any) {
    try {
      const response: any = await Promise.resolve() //TODO: endpoint correto
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function CLONE_INSPECTION(data: any) {
    try {
      const response: any = await inspectionServiceV2.clone(data)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function GET_PHOTO_PARAMETRIZATION(id: any) {
    try {
      const response: any = await inspectionPhotoServiceV2.getPhotoParametrization(id)
      return response
    } catch (e: any) {
      toast.error('Não foi possivel recuperar a parametrização de fotos desta vistoria.')
    }
  }

  async function GET_URL_UPLOAD(imagePaths: string[]) {
    try {
      const response: any = await inspectionPhotoServiceV2.getUrlUpload(imagePaths)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function UPLOAD_PHOTO(uploadUrl: string, file: File) {
    if (!uploadUrl) return
    try {
      const uploadResponse: any = await inspectionPhotoServiceV2.uploadPhoto(uploadUrl, file)
      return uploadResponse
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function UPDATE_INSPECTION_PHOTO(fileName: string, fullPath: string, params: any) {
    try {
      const payload = {
        fotos: [
          {
            ...params,
            nomeArquivo: fileName,
            caminhoArquivo: fullPath,
          },
        ],
      }
      const responseUpdatePhoto: any = await inspectionPhotoServiceV2.post(payload)
      return responseUpdatePhoto
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function UPDATE_INSPECTION_PHOTO_OBSERVATION(params: IUpdateInspectionPhotoObservation) {
    try {
      const responseUpdatePhoto: any = await inspectionPhotoServiceV2.updateObservation(params)
      return responseUpdatePhoto
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function ADD_INSPECTION_PHOTO(file: File, params: any) {
    try {
      const fileExtension = getFileExtension(file)
      const fileName = generateFilename('inspection', fileExtension)
      const fullPath = `${PHOTOS_PATHS.INSPECTION_PHOTOS}/${fileName}`

      const generatedUrlResponse = await GET_URL_UPLOAD([fullPath])
      if (!SUCCESS_STATUS.includes(generatedUrlResponse?.status))
        throw new Error(MESSAGES.generateUrlError)

      const generatedUrl = generatedUrlResponse?.data?.[0]?.data?.arquivoUrl
      if (!generatedUrl) throw new Error(MESSAGES.uploadError)

      const uploadedFiles = await UPLOAD_PHOTO(generatedUrl, file)
      if (!SUCCESS_STATUS.includes(uploadedFiles?.status)) throw new Error(MESSAGES.uploadError)

      const responseUpdatedPhoto = await UPDATE_INSPECTION_PHOTO(fileName, fullPath, params)
      return responseUpdatedPhoto
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function DISABLE_INSPECTION_PHOTO(params: IDisableInspectionPhotoParams) {
    try {
      const response: any = await inspectionPhotoServiceV2.disable(params)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const EXECUTE_INSPECTION = async (params: IFinalizeInspectionParams) => {
    try {
      const response: any = await inspectionServiceV2.finalize(params)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const CHECK_INSPECTION_REMAKE = async (inspection?: any) => {
    if (inspection.situacaoEnum === ESituacaoVistoria.Iniciada && inspection?.dataRealizacao) {
      SET_IS_INSPECTION_REMAKE(true)
      return
    }
    SET_IS_INSPECTION_REMAKE(false)
  }

  const REPROVE_INSPECTION_PHOTOS = async (params: IReproveInspectionPhotoParams) => {
    try {
      const response: any = await inspectionPhotoServiceV2.reprove(params)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const CREATE_INSPECTION_CHECKLIST = async (params: IInspectionChecklistPostParams) => {
    try {
      const response: any = await inspectionServiceV2.postChecklist(params)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const UPDATE_INSPECTION_CHECKLIST_STATUS = async (params: IInspectionChecklistPutParams) => {
    try {
      const response: any = await inspectionServiceV2.updateChecklistStatus(params)
      return response
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  return (
    <InspectionContext.Provider
      value={{
        INSPECTION_LIST,
        INTERPRISE_LIST,
        VEHICLE_PHOTO_DATA,
        BREAKDOWN_PHOTO_DATA,
        OWNER_DATA,
        INSPECTION_DATA,
        SELECTED_INSPECTION_DETAILS,
        loading,
        setLoading,
        ACCESSORIES_LIST,
        FLEET_LIST,
        CATEGORY_LIST,
        CATEGORY_OPTIONS,
        BOARD_LIST,
        BOARD_OPTIONS,
        MODEL_LIST,
        MODEL_OPTIONS,
        MODEL_CATEGORY_LIST,
        MODEL_CATEGORY_OPTIONS,
        CATEGORY_INSPECTION_LIST,
        IS_LEGACY_INSPECTION,
        IS_INSPECTION_REMAKE,
        HISTORY_CR,
        GET_INSPECTION,
        GET_INSPECTION_BY_ID,
        GET_INTERPRISE,
        GET_BREAKDOWN_PHOTOS,
        GET_VEHICLE_PHOTOS,
        GET_ACCESSORIES,
        GET_INSPECTION_HISTORY,
        SET_REFUSED,
        SET_REFUSE_ALL,
        SET_EVALUETE_FINISHED,
        SET_OWNER,
        SET_INSPECTION,
        SET_VEHICLE_PHOTO_DATA,
        SET_BREAKDOWN_PHOTO_DATA,
        GET_FLEET,
        GET_MODEL,
        GET_SELECTS,
        GET_SUBCATEGORIES,
        GET_SELECTED_INSPECTION_DETAILS,
        GET_CATEGORY_INSPECTION,
        GET_PHOTO_PARAMETRIZATION,
        GET_SIGNATURE,
        CREATE_INSPECTION,
        UPDATE_INSPECTION,
        CREATE_FLEET_INSPECTION,
        START_INSPECTION,
        UPDATE_INSPECTION_EXECUTION,
        UPDATE_INSPECTION_OWNER,
        CLONE_INSPECTION,
        ADD_INSPECTION_PHOTO,
        UPDATE_INSPECTION_PHOTO,
        DISABLE_INSPECTION_PHOTO,
        EXECUTE_INSPECTION,
        REPROVE_INSPECTION_PHOTOS,
        CREATE_INSPECTION_CHECKLIST,
        UPDATE_INSPECTION_CHECKLIST_STATUS,
        UPDATE_INSPECTION_PHOTO_OBSERVATION,
        GET_HISTORY_CR,
      }}
    >
      {children}
    </InspectionContext.Provider>
  )
}
